import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo, KenyaStripe } from "@/components/Logo";
import { toast } from "sonner";
import { CheckCircle2, UserPlus, ArrowRight, ShieldCheck, Crown, Gavel } from "lucide-react";

const ROLE_OPTIONS = [
  { value: "owner",        label: "Team Owner",   icon: Crown,       blurb: "I want to buy a franchise and own a team." },
  { value: "league_admin", label: "League Admin", icon: ShieldCheck, blurb: "I want to help run league operations." },
  { value: "court_umpire", label: "Court Umpire", icon: Gavel,       blurb: "I want to umpire and score matches." },
];

export default function Signup() {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", requested_role: "owner", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name || !form.email || !form.password) return toast.error("Name, email, and password are required");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    setSubmitting(true);
    try {
      await api.post("/auth/signup", form);
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSubmitting(false);
  };

  const selected = ROLE_OPTIONS.find((r) => r.value === form.requested_role);

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#060d09] p-6">
        <Card className="max-w-lg w-full p-10 text-center bg-[#11241b] border-[#00A86B]/40" data-testid="signup-success">
          <CheckCircle2 className="w-16 h-16 mx-auto text-[#00A86B] mb-4" />
          <h1 className="font-anton text-4xl uppercase tracking-tighter text-white">Request Submitted</h1>
          <KenyaStripe className="mt-3 w-24 mx-auto" />
          <p className="text-zinc-300 mt-5 leading-relaxed">
            Your <span className="text-white font-bold">{selected?.label}</span> account is now <span className="text-amber-400 font-bold">pending Super Admin approval</span>.
            You&apos;ll be able to sign in as soon as an admin approves your request — usually within 24 hours.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
            <Button asChild data-testid="signup-success-home" className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest">
              <Link to="/">Back to Home <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button asChild variant="outline" data-testid="signup-success-admin" className="border-white/20 text-white hover:bg-white/10">
              <Link to="/admin">Sign in →</Link>
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060d09]">
      <div className="kenya-stripe" />
      <header className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/"><Logo variant="inline" className="h-10" /></Link>
        <Link to="/admin" data-testid="signup-link-admin" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white">Already have access? Sign in →</Link>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <Card className="bg-[#11241b] border-white/10 p-8 md:p-10" data-testid="signup-card">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#00A86B] font-bold">Request Operator Access</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white mt-2">Apply For Access</h1>
          <KenyaStripe className="mt-3 w-24" />
          <p className="text-zinc-400 mt-5 max-w-lg">
            Fill in your details to request access as a Team Owner, League Admin, or Court Umpire. A Super Admin will
            review and approve your request.
          </p>

          {/* Role picker */}
          <div className="mt-8 grid sm:grid-cols-3 gap-3" data-testid="signup-role-grid">
            {ROLE_OPTIONS.map((r) => {
              const Icon = r.icon;
              const active = form.requested_role === r.value;
              return (
                <button
                  type="button"
                  key={r.value}
                  data-testid={`signup-role-${r.value}`}
                  onClick={() => setForm({ ...form, requested_role: r.value })}
                  className={`text-left rounded-lg p-4 border transition-colors ${active ? "border-[#00A86B] bg-[#00A86B]/10" : "border-white/10 bg-[#060d09] hover:border-white/30"}`}
                >
                  <Icon className={`w-5 h-5 mb-2 ${active ? "text-[#00A86B]" : "text-zinc-400"}`} />
                  <div className={`font-bold uppercase tracking-wide text-sm ${active ? "text-white" : "text-zinc-300"}`}>{r.label}</div>
                  <div className="text-[11px] text-zinc-500 mt-1">{r.blurb}</div>
                </button>
              );
            })}
          </div>

          <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Full name</Label>
              <Input data-testid="signup-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-[#060d09] border-white/10 text-white mt-1 h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
              <Input data-testid="signup-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="bg-[#060d09] border-white/10 text-white mt-1 h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Password</Label>
              <Input data-testid="signup-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required placeholder="min 6 characters" className="bg-[#060d09] border-white/10 text-white mt-1 h-11" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Tell us why you&apos;re applying (optional)</Label>
              <Textarea data-testid="signup-note" rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. I represent ABC Tech and want to own the franchise for Nairobi." className="bg-[#060d09] border-white/10 text-white mt-1" />
            </div>
            <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3 pt-2">
              <div className="text-[11px] text-zinc-500">By applying you agree your details will be reviewed by Mara Padel admins.</div>
              <Button
                data-testid="signup-submit"
                type="submit"
                disabled={submitting}
                className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest h-11 px-6"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {submitting ? "Submitting…" : "Submit Application"}
              </Button>
            </div>
          </form>
        </Card>

        <div className="mt-6 text-center text-xs text-zinc-500">
          Just want to play? <Link to="/register-player" data-testid="signup-link-player" className="text-[#00A86B] hover:underline">Register as a Player →</Link>
        </div>
      </div>
    </div>
  );
}
